import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { OrderService } from '@app/services/order.service';
import { ToastService } from '@app/services/toast.service';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { DialogService } from '@app/services/dialog.service';
import { CompaniesService } from '@app/services/companies.service';
import { AddressService } from '@app/services/address.service';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { map, catchError, timeout, finalize, debounceTime, distinctUntilChanged, filter, take, switchMap } from 'rxjs/operators';

interface SheetRow {
  id?: string;
  row_uuid?: any;
  rawValue: string;
  isDirty: boolean;
  original: any;
  selected?: boolean;
  saving?: boolean;
}

@Component({
  selector: 'app-dispatch-sheet',
  templateUrl: './dispatch-sheet.component.html',
  styleUrls: ['./dispatch-sheet.component.scss']
})
export class DispatchSheetComponent implements OnInit, OnDestroy {

  rows: SheetRow[] = [];
  isLoading = false;
  isSyncing = false;
  isExporting = false;
  isAccessingSheet = false;
  showSyncSheetButton = false;

  lastSyncedAt: Date | null = null;
  activeRowIndex: number | null = null;
  activeSegment = 0;
  isActivelyTyping = false;
  activeHeaderIndexOnFocus: number | null = null;
  activeCursorPos: number | null = null;
  unsavedCount = 0;
  deletedRowUuids: string[] = [];

  // Driver autocomplete dropdown
  driverDropdownVisible = false;
  driverDropdownItems: string[] = [];
  driverDropdownIndex = -1;
  driverDropdownLeft = 0;

  // Company autocomplete dropdown
  companyDropdownVisible = false;
  companyDropdownItems: any[] = [];
  companyDropdownIndex = -1;
  companyDropdownLeft = 0;

  // Address autocomplete dropdown
  addressDropdownVisible = false;
  addressDropdownItems: any[] = [];
  addressDropdownIndex = -1;
  addressDropdownLeft = 0;

  // Archived Sheets Modal
  showArchivedSheetsModal = false;
  isLoadingSheetsLogs = false;
  archivedSheetsLogs: any[] = [];
  logDateFilter = '';

  private companySearchSubject = new Subject<{ inputEl: HTMLInputElement; rawValue: string; searchText: string; rowIndex: number }>();
  private addressSearchSubject = new Subject<{ inputEl: HTMLInputElement; rawValue: string; searchText: string; rowIndex: number }>();
  private subscriptions = new Subscription();

  todayStr = (() => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  })();

  columnLabels = [
    'Order No*',
    'Pickup/Delivery*',
    'Company Name*',
    'Company Address*',
    'Contact Name',
    'Contact Phone No',
    'Contact Email',
    'Committed Time',
    'Del/PU',
    'Date (DD-MM-YYYY)',
    'Driver'
  ];
  isDeleting = false;

  get columnHintPairs() {
    return [
      { label: 'Order No*', example: '1231453' },
      { label: 'Pickup/Delivery*', example: 'Delivery' },
      { label: 'Company Name*', example: 'Lexicor' },
      { label: 'Company Address*', example: '1100 Sheppard' },
      { label: 'Contact Name', example: 'John Smith' },
      { label: 'Contact Phone No', example: '4161234567' },
      { label: 'Contact Email', example: 'info@lexicor.com' },
      { label: 'Committed Time', example: '10:00 AM' },
      { label: 'Del/PU', example: '0' },
      { label: 'Date (DD-MM-YYYY)', example: this.todayStr },
      { label: 'Driver', example: 'John Driver' }
    ];
  }

  getCleanLabel(label: string): string {
    return label.endsWith('*') ? label.slice(0, -1) : label;
  }

  constructor(
    private router: Router,
    private orderService: OrderService,
    private companyService: CompaniesService,
    private addressService: AddressService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef,
    private breadcrumbService: BreadcrumbService,
    public authService: AuthServiceService,
    private dialogService: DialogService
  ) { }

  ngOnInit(): void {
    this.syncFromSheet();


    // Set page breadcrumbs
    this.breadcrumbService.set([
      { label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', action: () => this.router.navigate(['/admin/dashboard']) },
      { label: 'Bulk Add Orders', action: () => this.router.navigate(['/admin/bulk-order']) },
      { label: 'Access Sheet' }
    ]);

    // Fetch active drivers list for name-to-id mapping
    this.orderService.getActiveDrivers().subscribe(data => {
      if (data?.result?.results) {
        this.driverList = data.result.results;
      } else if (data?.result) {
        this.driverList = data.result;
      }
    });

    // Debounced search setup (500ms delay as standardized across project)
    this.subscriptions.add(
      this.companySearchSubject.pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => prev.searchText === curr.searchText),
        filter(req => !!req.searchText && req.searchText.trim().length >= 1)
      ).subscribe(req => this.performCompanySearch(req))
    );

    this.subscriptions.add(
      this.addressSearchSubject.pipe(
        debounceTime(500),
        distinctUntilChanged((prev, curr) => prev.searchText === curr.searchText),
        filter(req => !!req.searchText && req.searchText.trim().length >= 1)
      ).subscribe(req => this.performAddressSearch(req))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }


  /**
   * Parse the backend API parsed response objects into the pipe-separated string format
   */
  driverList: any[] = [];

  private reconstructPipeString(item: any): string {
    const isValuable = (val: any): boolean => {
      if (val === null || val === undefined) return false;
      const str = val.toString().trim();
      const lower = str.toLowerCase();
      return (
        str.length > 0 &&
        lower !== 'null' &&
        lower !== 'undefined' &&
        lower !== '0' &&
        lower !== 'false'
      );
    };

    const orderNo = isValuable(item.order_no) ? item.order_no : '';
    const type = (item.warehouse || '').toLowerCase().startsWith('p') ? 'p' : 'd';
    const company = isValuable(item.company_name) ? item.company_name : '';
    const address = isValuable(item.address) ? item.address : (isValuable(item.customer_addr) ? item.customer_addr : '');
    const contactName = isValuable(item.contact_name) ? item.contact_name : (isValuable(item.customer_name) ? item.customer_name : '');
    const phone = isValuable(item.phone) ? item.phone : (isValuable(item.customer_phone) ? item.customer_phone : '');
    const email = isValuable(item.email) ? item.email : (isValuable(item.customer_email) ? item.customer_email : '');
    const committedTime = isValuable(item.dely_time) ? item.dely_time : (isValuable(item.pickup_time) ? item.pickup_time : '');
    const delPU = isValuable(item.first_delivery) ? item.first_delivery : '';
    const rawDate = isValuable(item.dely_date) ? item.dely_date.split('T')[0] : (isValuable(item.pickup_date) ? item.pickup_date.split('T')[0] : '');
    let date = '';
    if (rawDate && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      const dateParts = rawDate.split('-');
      date = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
    } else {
      date = rawDate;
    }
    const driver = isValuable(item.driver_name) ? item.driver_name : (item.current_driver ? `${item.current_driver.first_name || ''} ${item.current_driver.last_name || ''}`.trim() : '');

    // Filter out empty strings and join with a single spacer pipe
    const segments = [orderNo, type, company, address, contactName, phone, email, committedTime, delPU, date, driver];
    return segments
      .map(s => s.toString().trim())
      .filter(s => isValuable(s))
      .join(' | ') + ' |';
  }

  /**
   * Parse the pipe-separated raw value back into the structured object fields
   */
  private cleanOrderString(rawString: string): string {
    if (!rawString) return '';
    const parts = rawString.split('|').map(p => p.trim());

    // 1. Protect the first 4 mandatory slots (Order No, Type, Company, Address)
    const mandatory = parts.slice(0, 4);
    while (mandatory.length < 4) mandatory.push('');

    // 2. Filter out empty pipes for optional fields only (index 4 onwards)
    const optional = parts.slice(4).filter(p => p !== '');

    // 3. Rejoin safely
    return [...mandatory, ...optional].join(' | ');
  }

  private parsePipeString(rawValue: string): any {
    const result: any = {
      orderNo: '',
      warehouse: 'Delivery',
      companyName: '',
      companyAddress: '',
      contactName: '',
      phone: '',
      email: '',
      deliveryTime: null,
      pickupTime: null,
      deliveryDate: null,
      pickupDate: null,
      delPU: 0,
      driver_name: '',
      countryCode: 'CA',
      serviceTime: 't15',
      signReq: 'No'
    };

    if (!rawValue) return result;
    const rawParts = rawValue.split('|').map(p => p.trim());

    // 1. Strict mandatory positional matching (slots 0 to 3)
    if (rawParts.length > 0) result.orderNo = rawParts[0];
    let isPickup = false;
    if (rawParts.length > 1) {
      isPickup = rawParts[1].toLowerCase() === 'p' || rawParts[1].toLowerCase() === 'pickup';
      result.warehouse = rawParts[1] ? (isPickup ? 'Pickup' : 'Delivery') : '';
    }
    if (rawParts.length > 2) result.companyName = rawParts[2];
    if (rawParts.length > 3) result.companyAddress = rawParts[3];

    // 2. Match optional fields from slot 4 onwards
    const matchedIdx = new Set<number>();
    const parts = rawParts;

    for (let i = 4; i < parts.length; i++) {
      const val = parts[i];

      // Email
      if (val.includes('@') && !result.email) {
        result.email = val;
        matchedIdx.add(i);
        continue;
      }

      // Date
      const dateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}$|^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/;
      if (dateRegex.test(val) && !result.deliveryDate && !result.pickupDate) {
        if (isPickup) result.pickupDate = val;
        else result.deliveryDate = val;
        matchedIdx.add(i);
        continue;
      }

      // Time
      const timeRegex = /^\d{1,2}:\d{2}(?:\s*[APap][Mm])?$|^\d{1,2}\s*[APap][Mm]$/;
      if (timeRegex.test(val) && !result.deliveryTime && !result.pickupTime) {
        if (isPickup) result.pickupTime = val;
        else result.deliveryTime = val;
        matchedIdx.add(i);
        continue;
      }

      // Del/PU
      const numVal = Number(val);
      const isNumeric = val !== '' && !isNaN(numVal);
      if (isNumeric && numVal >= 0 && numVal <= 200 && result.delPU === 0) {
        result.delPU = numVal;
        matchedIdx.add(i);
        continue;
      }

      // Phone
      const cleanDigits = val.replace(/\D/g, '');
      const isDigitsOnly = /^\+?[\d\s\-\(\)]+$/.test(val);
      if (isDigitsOnly && cleanDigits.length >= 3 && !result.phone) {
        result.phone = val;
        matchedIdx.add(i);
        continue;
      }
    }

    // 3. Sequential Text Order for Contact Name and Driver
    const remainingTextIndices: number[] = [];
    for (let i = 4; i < parts.length; i++) {
      if (!matchedIdx.has(i)) {
        remainingTextIndices.push(i);
      }
    }

    if (remainingTextIndices.length === 1) {
      const idx = remainingTextIndices[0];
      const val = parts[idx];
      const valLower = val.toLowerCase();
      const isDriverMatch = (this.driverList && this.driverList.length > 0 && this.driverList.some(d => {
        const firstName = (d.first_name || '').trim().toLowerCase();
        const fullName = (d.first_name ? `${d.first_name} ${d.last_name || ''}` : d.name || d.driver_name || '').trim().toLowerCase();
        return (firstName && (valLower === firstName || valLower.startsWith(firstName))) ||
          (fullName && (valLower === fullName || valLower.startsWith(fullName)));
      })) || valLower === 'driver';

      if (isDriverMatch) {
        result.driver_name = val;
      } else {
        result.contactName = val;
      }
    } else if (remainingTextIndices.length >= 2) {
      const val0 = parts[remainingTextIndices[0]];
      const val1 = parts[remainingTextIndices[1]];

      const isDriverMatch = (val: string): boolean => {
        if (!val || !this.driverList || this.driverList.length === 0) return false;
        const vLower = val.trim().toLowerCase();
        return this.driverList.some(d => {
          const firstName = (d.first_name || '').trim().toLowerCase();
          const fullName = (d.first_name ? `${d.first_name} ${d.last_name || ''}` : d.name || d.driver_name || '').trim().toLowerCase();
          return (firstName && (vLower === firstName || vLower.startsWith(firstName))) ||
            (fullName && (vLower === fullName || vLower.startsWith(fullName)));
        });
      };

      const isDriver0 = isDriverMatch(val0);
      const isDriver1 = isDriverMatch(val1);

      if (isDriver0 && !isDriver1) {
        result.driver_name = val0;
        result.contactName = val1;
      } else if (isDriver1 && !isDriver0) {
        result.driver_name = val1;
        result.contactName = val0;
      } else {
        // Fallback: 1st text string is Contact Name, 2nd text string is Driver Name
        result.contactName = val0;
        result.driver_name = val1;
      }
    }

    return result;
  }

  syncFromSheet(): void {
    this.isSyncing = true;
    const isInitialLoad = this.rows.length === 0;
    this.isLoading = isInitialLoad;
    this.cdr.markForCheck();

    this.orderService.getDispatchSheetRows().subscribe({
      next: (response: any) => {
        this.isSyncing = false;
        this.isLoading = false;
        if (response?.resultCode === '1' && Array.isArray(response.result)) {
          const isZeroOrders = response.resultDescription && response.resultDescription.toLowerCase().includes('0 order');
          const isEmptyResult = response.result.length === 0;
          if (isZeroOrders || isEmptyResult) {
            this.toast.show('warning', response.resultDescription || 'No data found in the sheet');
          } else if (!isInitialLoad) {
            this.toast.show('success', 'Sheet synced with latest server data');
          }

          const hasDirtyRows = this.rows.some(r => r.isDirty && r.rawValue.trim() !== '');
          if (!isInitialLoad && hasDirtyRows) {
            this.mergeServerRows(response.result);
          } else {
            this.rows = response.result.map((item: any) => ({
              id: item.order_id || item.id || '',
              row_uuid: item.row_uuid || '',
              rawValue: this.cleanOrderString(item.order_string || ''),
              isDirty: false,
              original: item
            }));
          }

          this.deletedRowUuids = [];
          this.lastSyncedAt = new Date();
          this.updateUnsavedCount();
        } else {
          this.toast.show('error', response?.resultDescription || 'Failed to sync sheet');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSyncing = false;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  exportSheet(): void {
    this.isExporting = true;
    this.cdr.markForCheck();
    this.orderService.getDispatchSheetUrl(true).subscribe({
      next: (blob: Blob) => {
        this.isExporting = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DispatchSheet_${this.todayStr}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.toast.show('success', 'Sheet exported successfully');
      },
      error: () => {
        this.isExporting = false;
        this.toast.show('error', 'Failed to export sheet');
        this.cdr.markForCheck();
      }
    });
  }

  accessGoogleSheet(): void {
    this.isAccessingSheet = true;
    this.cdr.markForCheck();
    this.orderService.getDispatchSheetUrl(false).subscribe({
      next: (res: any) => {
        this.isAccessingSheet = false;
        if (res?.result?.sheet_url) {
          window.open(res.result.sheet_url, '_blank');
        } else if (res?.sheet_url) {
          window.open(res.sheet_url, '_blank');
        } else {
          this.toast.show('error', 'Google Sheet URL not available');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isAccessingSheet = false;
        this.toast.show('error', 'Failed to fetch Google Sheet URL');
        this.cdr.markForCheck();
      }
    });
  }

  // Row operations
  addRow(): void {
    const newRow: SheetRow = {
      rawValue: '',
      isDirty: true,
      original: null
    };
    this.rows.push(newRow);
    this.activeRowIndex = this.rows.length - 1;
    this.updateUnsavedCount();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  addMultipleRows(count: number): void {
    for (let i = 0; i < count; i++) {
      this.rows.push({
        rawValue: '',
        isDirty: true,
        original: null
      });
    }
    this.activeRowIndex = this.rows.length - 1;
    this.updateUnsavedCount();
    this.cdr.markForCheck();
  }

  deleteRow(index: number): void {
    const row = this.rows[index];
    if (!row) return;

    // Splice immediately for instant visual deletion
    this.rows.splice(index, 1);
    if (this.activeRowIndex !== null && this.activeRowIndex >= this.rows.length) {
      this.activeRowIndex = this.rows.length > 0 ? this.rows.length - 1 : null;
    }

    const uuid = row.row_uuid || row.id;
    if (uuid && !this.deletedRowUuids.includes(uuid.toString())) {
      this.deletedRowUuids.push(uuid.toString());
    }

    this.updateUnsavedCount();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    this.saveAll();
  }

  isAllSelected(): boolean {
    if (this.rows.length === 0) return false;
    return this.rows.every(r => r.selected);
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.forEach(r => r.selected = checked);
    this.cdr.markForCheck();
  }

  hasSelectedRows(): boolean {
    return this.rows.some(r => r.selected);
  }

  getSelectedCount(): number {
    return this.rows.filter(r => r.selected).length;
  }

  async deleteSelectedRows(): Promise<void> {
    const selectedRows = this.rows.filter(r => r.selected);
    if (selectedRows.length === 0) return;

    const dialogRes = await this.dialogService.confirm(
      'Delete Selected Rows',
      `Are you sure you want to delete the ${selectedRows.length} selected row(s)?`
    );
    if (!dialogRes || !dialogRes.confirmed) {
      return;
    }

    selectedRows.forEach(row => {
      const uuid = row.row_uuid || row.id;
      if (uuid && !this.deletedRowUuids.includes(uuid.toString())) {
        this.deletedRowUuids.push(uuid.toString());
      }
    });

    // Remove selected rows locally
    this.rows = this.rows.filter(r => !r.selected);

    // Reset activeRowIndex if it's now out of bounds
    if (this.activeRowIndex !== null && this.activeRowIndex >= this.rows.length) {
      this.activeRowIndex = this.rows.length > 0 ? this.rows.length - 1 : null;
    }

    this.updateUnsavedCount();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    this.saveAll();
  }

  saveRow(index: number): void {
    this.resolveRowShortcuts(index);
    this.capitalizeRowValues(index);
    const row = this.rows[index];
    if (!row) return;
    if (row.saving) return;

    if (!row.rawValue.trim()) {
      this.toast.show('error', 'Row data cannot be empty');
      return;
    }

    const parsed = this.parsePipeString(row.rawValue);

    // Minimal validation
    if (!parsed.orderNo || !parsed.companyName || !parsed.companyAddress) {
      this.toast.show('error', 'Row must contain at least Order No, Order Type, Company Name, and Company Address');
      return;
    }

    const action = row.row_uuid ? 'edit' : 'add';
    row.saving = true;

    this.orderService.addOrdersToDispatchSheet(row.rawValue, action, row.row_uuid).pipe(
      timeout(15000),
      finalize(() => {
        row.saving = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response: any) => {
        if (response.resultCode === '1') {
          this.toast.show('success', action === 'edit' ? 'Row updated successfully' : 'Row saved and synced to Google Sheet');

          // Backend is the single source of truth: update rows directly from API response
          if (Array.isArray(response.result)) {
            this.rows = response.result.map((item: any) => ({
              id: item.order_id || item.id || '',
              row_uuid: item.row_uuid || '',
              rawValue: this.cleanOrderString(item.order_string || ''),
              isDirty: false,
              original: item
            }));
            this.deletedRowUuids = [];
            this.updateUnsavedCount();
          } else {
            row.isDirty = false;
          }
        } else {
          this.toast.show('error', response.resultDescription || response.errorMessage || 'Failed to save row');
        }
      },
      error: () => {
        this.toast.show('error', 'An error occurred or request timed out while saving row');
      }
    });
  }

  saveAll(): void {
    // Prevent concurrent sync operations (delete, sync button, etc. can all call saveAll)
    if (this.isSyncing) return;

    // Resolve shortcuts and capitalize dirty row values first
    this.rows.forEach((row, idx) => {
      if (row.isDirty && row.rawValue.trim()) {
        this.resolveRowShortcuts(idx);
        this.capitalizeRowValues(idx);
      }
    });

    // Validate all dirty rows
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      if (row.isDirty && row.rawValue.trim() && !row.saving) {
        const parsed = this.parsePipeString(row.rawValue);
        if (!parsed.orderNo || !parsed.companyName || !parsed.companyAddress) {
          this.toast.show('error', `Row #${i + 1} must contain at least Order No, Order Type, Company Name, and Company Address`);
          return;
        }
      }
    }

    this.isSyncing = true;
    this.cdr.markForCheck();

    let currentDirtyRows: any[] = [];
    let deletionsToSend: string[] = [];

    this.orderService.getDispatchSheetRows().pipe(
      switchMap((getRes: any) => {
        if (!getRes || getRes.resultCode !== '1') {
          throw new Error(getRes?.resultDescription || getRes?.errorMessage || 'Failed to fetch latest sheet data');
        }

        if (Array.isArray(getRes.result)) {
          this.mergeServerRows(getRes.result, false);
        }

        currentDirtyRows = this.rows.filter(r => r.isDirty && r.rawValue.trim() && !r.saving);
        deletionsToSend = Array.from(new Set(this.deletedRowUuids));

        if (currentDirtyRows.length === 0 && deletionsToSend.length === 0) {
          this.lastSyncedAt = new Date();
          this.toast.show('success', getRes.resultDescription || 'Sheet synced with latest server data');
          return of({ skipped: true });
        }

        const addPayload: string[] = [];
        const editPayload: { row_uuid: string; order_string: string }[] = [];

        currentDirtyRows.forEach(row => {
          row.saving = true; // Lock row
          row._sentValue = row.rawValue; // Snapshot the string we are sending

          if (row.row_uuid) {
            editPayload.push({
              row_uuid: row.row_uuid,
              order_string: row.rawValue
            });
          } else {
            addPayload.push(row.rawValue);
          }
        });

        const syncPayload = {
          action: 'sync_changes' as const,
          add: addPayload,
          edit: editPayload,
          delete: deletionsToSend
        };

        return this.orderService.syncDispatchSheetChanges(syncPayload);
      }),
      catchError((err: any) => {
        this.toast.show('error', err?.message || 'An error occurred during sync');
        return of({ error: true }); // Return safe object to bypass success block
      }),
      finalize(() => {
        this.isSyncing = false;
        // Since mergeServerRows creates new object references, we must iterate over this.rows
        this.rows.forEach(row => {
          if ((row as any)._sentValue !== undefined) {
            row.saving = false;
            delete (row as any)._sentValue; // Clean up memory here so finalize can read it first
          }
        });
        // Fallback for original references in case mergeServerRows wasn't called
        currentDirtyRows.forEach(row => {
          row.saving = false;
          delete (row as any)._sentValue;
        });
        this.updateUnsavedCount();
        this.cdr.markForCheck();
      })
    ).subscribe((syncRes: any) => {
      if (!syncRes || syncRes.error || syncRes.skipped) return;

      if (syncRes.resultCode === '1') {
        this.toast.show('success', syncRes.resultDescription || 'Changes saved and synced successfully');

        if (Array.isArray(syncRes.result)) {
          this.mergeServerRows(syncRes.result, false);
        }

        // Clear dirty states on the newly created row objects in this.rows
        this.rows.forEach(row => {
          if ((row as any)._sentValue !== undefined) {
            row.isDirty = false;
            // Removed 'delete row._sentValue' from here; it's now handled safely in finalize()
          }
        });

        // Safely filter out ONLY the UUIDs we successfully deleted
        this.deletedRowUuids = this.deletedRowUuids.filter(id => !deletionsToSend.includes(id));
        this.lastSyncedAt = new Date();
      } else {
        this.toast.show('error', syncRes?.resultDescription || syncRes?.errorMessage || 'Failed to save changes');
      }
    });
  }

  onRowFocus(index: number): void {
    this.activeRowIndex = index;
    this.isActivelyTyping = false;
  }

  resolveRowShortcuts(index: number): void {
    const row = this.rows[index];
    if (!row || !row.rawValue) return;

    const parts = row.rawValue.split('|').map(s => s.trim());
    while (parts.length > 0 && parts[parts.length - 1] === '') {
      parts.pop();
    }

    if (parts.length < 4) return;

    let accumulatedText = parts.slice(0, 4).join(' | ') + ' | ';

    for (let i = 4; i < parts.length; i++) {
      const seg = parts[i];
      if (!seg) continue;
      const testText = accumulatedText + seg;
      const res = this.evaluateShortcuts(testText);
      if (res.replaced) {
        accumulatedText = res.newBefore;
      } else {
        accumulatedText = testText + ' | ';
      }
    }

    row.rawValue = accumulatedText;
  }

  capitalizeRowValues(index: number): void {
    const row = this.rows[index];
    if (row && row.rawValue) {
      const parts = row.rawValue.split('|').map(s => s.trim());
      while (parts.length > 0 && parts[parts.length - 1] === '') {
        parts.pop();
      }
      const parsed = this.parsePipeString(row.rawValue);

      const capitalizedParts = parts.map((part, idx) => {
        const trimmed = part.trim();
        if (!trimmed) return part;

        // 1. Company Name is always index 2
        if (idx === 2) {
          return this.capitalizeWords(part);
        }
        // 2. Optional fields match
        if (idx >= 4) {
          if (trimmed === parsed.contactName) {
            return this.capitalizeWords(part);
          }
          if (trimmed === parsed.driver_name) {
            return this.capitalizeWords(part);
          }
        }
        return part;
      });

      row.rawValue = capitalizedParts.join(' | ') + ' |';
    }
  }

  capitalizeWords(val: string): string {
    if (!val) return '';
    return val.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onRowBlur(index: number): void {
    this.isActivelyTyping = false;
    this.capitalizeRowValues(index);

    // Keep active if tabbed/focused, but let's clear activeRowIndex
    setTimeout(() => {
      if (this.activeRowIndex === index) {
        this.activeRowIndex = null;
        this.cdr.markForCheck();
      }
    }, 200);
  }

  onRowInput(index: number, event: Event): void {
    this.isActivelyTyping = true;
    const inputEl = event.target as HTMLInputElement;
    const originalValue = inputEl.value;
    let value = originalValue;
    const selectionStart = inputEl.selectionStart;

    // Auto-expand 'p' or 'd' to 'Pickup' or 'Delivery' in the second segment (index 1)
    let cursorOffset = 0;
    const parts = value.split('|');
    if (parts.length >= 2 && !this.isDeleting) {
      const pdSegment = parts[1];
      const trimmed = pdSegment.trim();
      if (trimmed.toLowerCase() === 'p') {
        parts[1] = pdSegment.replace(/p/i, 'Pickup');
        value = parts.join('|');
        cursorOffset = 5; // "Pickup".length - "p".length
      } else if (trimmed.toLowerCase() === 'd') {
        parts[1] = pdSegment.replace(/d/i, 'Delivery');
        value = parts.join('|');
        cursorOffset = 7; // "Delivery".length - "d".length
      }
    }

    // Limit phone segment digits to max 10
    const processedParts = value.split('|');
    let didTruncatePhone = false;
    for (let i = 4; i < processedParts.length; i++) {
      const val = processedParts[i];
      const cleanDigits = val.replace(/\D/g, '');
      const isDigitsOnly = /^\+?[\d\s\-\(\)]+$/.test(val.trim());

      const dateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}$|^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/;
      const timeRegex = /^\d{1,2}:\d{2}(?:\s*[APap][Mm])?$|^\d{1,2}\s*[APap][Mm]$/;
      const numVal = Number(val.trim());
      const isNumeric = val.trim() !== '' && !isNaN(numVal);
      const isDelPU = isNumeric && numVal >= 0 && numVal <= 200;

      if (isDigitsOnly && !dateRegex.test(val.trim()) && !timeRegex.test(val.trim()) && !isDelPU && cleanDigits.length > 10) {
        let digitCount = 0;
        let truncated = '';
        for (let charIdx = 0; charIdx < val.length; charIdx++) {
          const char = val[charIdx];
          if (/\d/.test(char)) {
            if (digitCount < 10) {
              truncated += char;
              digitCount++;
            }
          } else {
            truncated += char;
          }
        }
        processedParts[i] = truncated;
        didTruncatePhone = true;
      }
    }

    if (didTruncatePhone) {
      value = processedParts.join('|');
    }

    // Clean up any period right before a pipe
    const cleanedValue = value.replace(/\.\s*\|\s*/g, ' | ');

    if (cleanedValue !== originalValue) {
      this.rows[index].rawValue = cleanedValue;
      inputEl.value = cleanedValue;

      // Adjust cursor position based on changes made
      const diff = originalValue.length - cleanedValue.length;
      const newCursorPos = selectionStart !== null ? selectionStart + cursorOffset - diff : cleanedValue.length;
      inputEl.setSelectionRange(newCursorPos, newCursorPos);
    } else {
      this.rows[index].rawValue = value;
      if (cursorOffset > 0 && selectionStart !== null) {
        const newCursorPos = selectionStart + cursorOffset;
        inputEl.setSelectionRange(newCursorPos, newCursorPos);
      }
    }

    this.rows[index].isDirty = true;
    this.updateUnsavedCount();
    this.refreshActiveSegment(this.rows[index].rawValue || '', inputEl);
    this.updateContextualDropdowns(inputEl, this.rows[index].rawValue || '', index);
  }


  onKeyDown(event: KeyboardEvent, index: number): void {
    this.isDeleting = (event.key === 'Backspace' || event.key === 'Delete');

    // Company dropdown navigation
    if (this.companyDropdownVisible) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.companyDropdownIndex = Math.min(this.companyDropdownIndex + 1, this.companyDropdownItems.length - 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.companyDropdownIndex = Math.max(this.companyDropdownIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter' && this.companyDropdownIndex >= 0) {
        event.preventDefault();
        const selected = this.companyDropdownItems[this.companyDropdownIndex];
        const nameStr = selected.company || selected.company_name || selected.name || selected;
        this.acceptCompanyItem(nameStr, event.target as HTMLInputElement, index, selected);
        return;
      }
      if (event.key === 'Tab') {
        this.closeCompanyDropdown();
      }
      if (event.key === 'Escape') {
        this.closeCompanyDropdown();
        return;
      }
    }

    // Address dropdown navigation
    if (this.addressDropdownVisible) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.addressDropdownIndex = Math.min(this.addressDropdownIndex + 1, this.addressDropdownItems.length - 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.addressDropdownIndex = Math.max(this.addressDropdownIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter' && this.addressDropdownIndex >= 0) {
        event.preventDefault();
        const selected = this.addressDropdownItems[this.addressDropdownIndex];
        const addrStr = selected.address || selected.street_address || selected.name || selected;
        this.acceptAddressItem(addrStr, event.target as HTMLInputElement, index, selected);
        return;
      }
      if (event.key === 'Tab') {
        this.closeAddressDropdown();
      }
      if (event.key === 'Escape') {
        this.closeAddressDropdown();
        return;
      }
    }

    // Driver dropdown navigation (Untouched!)
    if (this.driverDropdownVisible) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.driverDropdownIndex = Math.min(this.driverDropdownIndex + 1, this.driverDropdownItems.length - 1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.driverDropdownIndex = Math.max(this.driverDropdownIndex - 1, 0);
        return;
      }
      if (event.key === 'Enter' && this.driverDropdownIndex >= 0) {
        event.preventDefault();
        this.acceptDropdownItem(this.driverDropdownItems[this.driverDropdownIndex], event.target as HTMLInputElement, index);
        return;
      }
      if (event.key === 'Tab') {
        this.closeDriverDropdown();
      }
      if (event.key === 'Escape') {
        this.closeDriverDropdown();
        return;
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeyDown(event: KeyboardEvent): void {
    const isCopy = (event.ctrlKey || event.metaKey) && event.key === 'c';
    if (!isCopy) return;

    // Only intercept if a row input inside this sheet is focused
    const active = document.activeElement as HTMLElement;
    if (!active || !active.closest('app-dispatch-sheet')) return;

    // Don't intercept if the user has selected text inside an input
    const inputEl = active as HTMLInputElement;
    if (inputEl.tagName === 'INPUT' && inputEl.selectionStart !== inputEl.selectionEnd) {
      return; // Let normal text copy happen
    }

    event.preventDefault();
    this.copySelectedRows();
  }

  copySelectedRows(): void {
    const selectedRows = this.rows.filter(r => r.selected);
    const rowsToCopy = selectedRows.length > 0
      ? selectedRows
      : (this.activeRowIndex !== null ? [this.rows[this.activeRowIndex]] : []);

    if (rowsToCopy.length === 0) {
      this.toast.show('error', 'No rows selected to copy');
      return;
    }

    const text = rowsToCopy
      .map(r => r.rawValue.split('|').map(s => s.trim()).join('\t'))
      .join('\n');

    navigator.clipboard.writeText(text).then(() => {
      const label = rowsToCopy.length === 1 ? '1 row' : `${rowsToCopy.length} rows`;
      this.toast.show('success', `${label} copied to clipboard`);
    }).catch(() => {
      this.toast.show('error', 'Failed to copy — clipboard access denied');
    });
  }

  updateActiveSegment(event: Event, rawValue: string): void {
    const inputEl = event.target as HTMLInputElement;
    if (inputEl && inputEl.selectionStart !== null) {
      this.activeCursorPos = inputEl.selectionStart;
    }
    if (event.type === 'click' || event.type === 'focus') {
      this.isActivelyTyping = false;
      if (inputEl && inputEl.selectionStart !== null) {
        this.activeCursorPos = inputEl.selectionStart;
      }
      this.refreshActiveSegment(rawValue, inputEl);
      this.activeHeaderIndexOnFocus = this.getActiveColumnIndex();
    } else {
      this.refreshActiveSegment(rawValue, inputEl);
    }
    if (inputEl) {
      this.updateContextualDropdowns(inputEl, rawValue, this.activeRowIndex !== null ? this.activeRowIndex : undefined);
    }
  }

  updateDriverDropdown(inputEl: HTMLInputElement, rawValue: string): void {
    if (!rawValue || !this.driverList || this.driverList.length === 0) {
      this.closeDriverDropdown();
      return;
    }

    const cursorPos = inputEl.selectionStart !== null ? inputEl.selectionStart : rawValue.length;
    const textBeforeCursor = rawValue.substring(0, cursorPos);
    const pipesCount = (textBeforeCursor.match(/\|/g) || []).length;

    // Do not show driver dropdown in mandatory fields (Order No, Pickup/Delivery, Company Name, Company Address)
    if (pipesCount < 4) {
      this.closeDriverDropdown();
      return;
    }

    const lastPipeIdx = textBeforeCursor.lastIndexOf('|');
    const typedSegVal = textBeforeCursor.substring(lastPipeIdx + 1).trim();

    if (!typedSegVal || typedSegVal.length < 1) {
      this.closeDriverDropdown();
      return;
    }

    const typedLower = typedSegVal.toLowerCase();

    // Check if a driver is already completed in another segment
    const rawParts = rawValue.split('|').map(p => p.trim());
    const currentSegIdx = pipesCount;

    const hasDriverInOtherSegment = rawParts.some((part, idx) => {
      if (idx === currentSegIdx || !part) return false;
      const pLower = part.toLowerCase();
      return !!this.driverList && this.driverList.some(d => {
        const firstName = (d.first_name || '').trim().toLowerCase();
        const fullName = (d.first_name ? `${d.first_name}${d.last_name ? ' ' + d.last_name : ''}` : d.name || d.driver_name || '').trim().toLowerCase();
        return (firstName && pLower === firstName) || (fullName && pLower === fullName);
      });
    });

    if (hasDriverInOtherSegment) {
      this.closeDriverDropdown();
      return;
    }

    // Filter matching drivers (up to 5 matches)
    const matches: string[] = [];
    for (const d of this.driverList) {
      const fullName = (d.first_name ? `${d.first_name}${d.last_name ? ' ' + d.last_name : ''}` : d.name || d.driver_name || d.firstName || '').trim();
      if (fullName && fullName.toLowerCase().startsWith(typedLower) && fullName.toLowerCase() !== typedLower) {
        if (!matches.includes(fullName)) {
          matches.push(fullName);
        }
      }
      if (matches.length >= 5) break;
    }

    if (matches.length > 0) {
      this.driverDropdownItems = matches;
      this.driverDropdownVisible = true;
      if (this.driverDropdownIndex < 0 || this.driverDropdownIndex >= matches.length) {
        this.driverDropdownIndex = 0;
      }

      const textBeforeSegment = lastPipeIdx >= 0 ? textBeforeCursor.substring(0, lastPipeIdx + 1) : '';
      this.driverDropdownLeft = this.calculateDropdownLeft(inputEl, textBeforeSegment);
    } else {
      this.closeDriverDropdown();
    }
  }

  acceptDropdownItem(driverName: string, inputEl: HTMLInputElement, rowIndex: number): void {
    const rawValue = this.rows[rowIndex].rawValue || '';
    const cursorPos = inputEl.selectionStart !== null ? inputEl.selectionStart : rawValue.length;
    const textBeforeCursor = rawValue.substring(0, cursorPos);
    const textAfterCursor = rawValue.substring(cursorPos);

    const lastPipeIdx = textBeforeCursor.lastIndexOf('|');
    const segmentBefore = lastPipeIdx >= 0 ? textBeforeCursor.substring(0, lastPipeIdx + 1) : '';
    const prefixSpace = segmentBefore && !segmentBefore.endsWith(' ') ? ' ' : '';

    let nextPipeOrEnd = textAfterCursor;
    const nextPipeIdx = textAfterCursor.indexOf('|');
    if (nextPipeIdx >= 0) {
      nextPipeOrEnd = textAfterCursor.substring(nextPipeIdx);
    } else {
      nextPipeOrEnd = '';
    }

    const hasPipeAfter = nextPipeOrEnd.startsWith('|') || nextPipeOrEnd.trim().startsWith('|');
    const trailing = hasPipeAfter ? '' : ' | ';

    const newBefore = segmentBefore + prefixSpace + driverName + trailing;
    const newValue = newBefore + nextPipeOrEnd.replace(/^\|/, ' |').trimStart();

    this.rows[rowIndex].rawValue = newValue;
    inputEl.value = newValue;

    const newPos = newBefore.length;
    inputEl.setSelectionRange(newPos, newPos);

    this.rows[rowIndex].isDirty = true;
    this.updateUnsavedCount();
    this.refreshActiveSegment(newValue, inputEl);
    this.closeDriverDropdown();
  }

  closeDriverDropdown(): void {
    this.driverDropdownVisible = false;
    this.driverDropdownItems = [];
    this.driverDropdownIndex = -1;
  }

  // --- Contextual Autocomplete Dropdown Router ---
  updateContextualDropdowns(inputEl: HTMLInputElement, rawValue: string, rowIndex?: number): void {
    if (!rawValue) {
      this.closeCompanyDropdown();
      this.closeAddressDropdown();
      this.closeDriverDropdown();
      return;
    }

    const cursorPos = inputEl.selectionStart !== null ? inputEl.selectionStart : rawValue.length;
    const textBeforeCursor = rawValue.substring(0, cursorPos);
    const pipesCount = (textBeforeCursor.match(/\|/g) || []).length;
    const lastPipeIdx = textBeforeCursor.lastIndexOf('|');
    const typedSegVal = textBeforeCursor.substring(lastPipeIdx + 1).trimStart();

    // Segment 2: Company Name (pipesCount === 2)
    if (pipesCount === 2) {
      this.closeAddressDropdown();
      this.closeDriverDropdown();
      this.updateCompanyDropdown(inputEl, rawValue, typedSegVal, lastPipeIdx, rowIndex);
      return;
    }

    // Segment 3: Company Address (pipesCount === 3)
    if (pipesCount === 3) {
      this.closeCompanyDropdown();
      this.closeDriverDropdown();
      this.updateAddressDropdown(inputEl, rawValue, typedSegVal, lastPipeIdx, rowIndex);
      return;
    }

    // Segment 4+: Driver dropdown or other fields
    this.closeCompanyDropdown();
    this.closeAddressDropdown();
    this.updateDriverDropdown(inputEl, rawValue);
  }

  // --- Company Dropdown Methods ---
  updateCompanyDropdown(inputEl: HTMLInputElement, rawValue: string, typedSegVal: string, lastPipeIdx: number, rowIndex?: number): void {
    if (!typedSegVal || typedSegVal.length < 1) {
      this.closeCompanyDropdown();
      return;
    }

    if (rowIndex !== undefined) {
      this.companySearchSubject.next({ inputEl, rawValue, searchText: typedSegVal, rowIndex });
    }
  }

  performCompanySearch(req: { inputEl: HTMLInputElement; rawValue: string; searchText: string; rowIndex: number }): void {
    this.companyService.allCompanySearch(1, req.searchText, undefined, false, 30).pipe(take(1)).subscribe({
      next: (data: any) => {
        let results = data?.addresses?.results || data?.companies?.results || data?.result?.results || data?.result || data || [];
        if (!Array.isArray(results) && typeof results === 'object') {
          results = results.results || results.addresses?.results || results.companies?.results || [];
        }
        if (Array.isArray(results) && results.length > 0) {
          this.companyDropdownItems = results;
          this.companyDropdownVisible = true;
          this.companyDropdownIndex = 0;
          const cursorPos = req.inputEl.selectionStart !== null ? req.inputEl.selectionStart : req.rawValue.length;
          const textBeforeCursor = req.rawValue.substring(0, cursorPos);
          const lastPipeIdx = textBeforeCursor.lastIndexOf('|');
          const textBeforeSegment = lastPipeIdx >= 0 ? textBeforeCursor.substring(0, lastPipeIdx + 1) : '';
          this.companyDropdownLeft = this.calculateDropdownLeft(req.inputEl, textBeforeSegment);
          this.cdr.markForCheck();
        }
      },
      error: () => this.closeCompanyDropdown()
    });
  }

  acceptCompanyItem(companyName: string, inputEl: HTMLInputElement, rowIndex: number, item?: any): void {
    this.updateSegmentValue(2, companyName, inputEl, rowIndex);
    this.closeCompanyDropdown();
  }

  closeCompanyDropdown(): void {
    this.companyDropdownVisible = false;
    this.companyDropdownItems = [];
    this.companyDropdownIndex = -1;
  }

  // --- Address Dropdown Methods ---
  updateAddressDropdown(inputEl: HTMLInputElement, rawValue: string, typedSegVal: string, lastPipeIdx: number, rowIndex?: number): void {
    if (!typedSegVal || typedSegVal.length < 1) {
      this.closeAddressDropdown();
      return;
    }

    if (rowIndex !== undefined) {
      this.addressSearchSubject.next({ inputEl, rawValue, searchText: typedSegVal, rowIndex });
    }
  }

  performAddressSearch(req: { inputEl: HTMLInputElement; rawValue: string; searchText: string; rowIndex: number }): void {
    this.addressService.getAllAddressListing(1, '', req.searchText, 30).pipe(take(1)).subscribe({
      next: (data: any) => {
        let results = data?.addresses?.results || data?.companies?.results || data?.result?.results || data?.result || data || [];
        if (!Array.isArray(results) && typeof results === 'object') {
          results = results.results || results.addresses?.results || results.companies?.results || [];
        }
        if (Array.isArray(results) && results.length > 0) {
          this.addressDropdownItems = results;
          this.addressDropdownVisible = true;
          this.addressDropdownIndex = 0;
          const cursorPos = req.inputEl.selectionStart !== null ? req.inputEl.selectionStart : req.rawValue.length;
          const textBeforeCursor = req.rawValue.substring(0, cursorPos);
          const lastPipeIdx = textBeforeCursor.lastIndexOf('|');
          const textBeforeSegment = lastPipeIdx >= 0 ? textBeforeCursor.substring(0, lastPipeIdx + 1) : '';
          this.addressDropdownLeft = this.calculateDropdownLeft(req.inputEl, textBeforeSegment);
          this.cdr.markForCheck();
        }
      },
      error: () => this.closeAddressDropdown()
    });
  }

  acceptAddressItem(addressText: string, inputEl: HTMLInputElement, rowIndex: number, item?: any): void {
    this.updateSegmentValue(3, addressText, inputEl, rowIndex);
    this.closeAddressDropdown();
  }

  closeAddressDropdown(): void {
    this.addressDropdownVisible = false;
    this.addressDropdownItems = [];
    this.addressDropdownIndex = -1;
  }

  // --- Display Label Helpers ---
  getCompanyLabel(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.company || item.company_name || item.name || '';
  }

  getAddressLabel(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.address || item.street_address || item.customer_addr || item.location || item.name || '';
  }

  private getTextWidthPixels(text: string, font: string = '14px monospace'): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text.length * 8.4; // fallback
    context.font = font;
    return context.measureText(text).width;
  }

  // Helper to position dropdown popovers cleanly
  private calculateDropdownLeft(inputEl: HTMLInputElement, textBeforeSegment: string): number {
    const textWidth = this.getTextWidthPixels(textBeforeSegment);
    const scrollOffset = inputEl ? (inputEl.parentElement?.scrollLeft || inputEl.scrollLeft || 0) : 0;
    // 70px (row-num) + 16px (cell-wrapper padding-left) = 86px starting offset
    const calculatedLeft = 86 + textWidth - scrollOffset;
    return Math.max(75, calculatedLeft); // Removed 700px ceiling
  }

  // Generic helper to set a specific pipe segment in rawValue
  private updateSegmentValue(segmentIndex: number, newValueStr: string, inputEl: HTMLInputElement, rowIndex: number): void {
    const rawValue = this.rows[rowIndex].rawValue || '';
    const parts = rawValue.split('|').map(p => p.trim());

    // Ensure array has enough elements up to segmentIndex
    while (parts.length <= segmentIndex) {
      parts.push('');
    }

    parts[segmentIndex] = newValueStr;

    // For Company Name (2) OR Address (3), ensure the next field's pipe delimiter exists
    if ((segmentIndex === 2 || segmentIndex === 3) && parts.length <= segmentIndex + 1) {
      parts.push('');
    }

    // Join back with existing pipe structure
    let newRawValue = parts.join(' | ');
    if (rawValue.trim().endsWith('|') && !newRawValue.endsWith('|')) {
      newRawValue += ' |';
    }
    this.rows[rowIndex].rawValue = newRawValue;
    inputEl.value = newRawValue;

    // Advance cursor to next segment if available (e.g., Segment 3 for Address after selecting Company)
    let targetSegment = segmentIndex;
    let targetOffset = parts.slice(0, segmentIndex + 1).join(' | ').length;

    if (parts.length > segmentIndex + 1) {
      targetSegment = segmentIndex + 1;
      targetOffset = targetOffset + 3;
    }

    this.activeRowIndex = rowIndex;
    this.activeSegment = targetSegment;

    if (inputEl) {
      inputEl.focus();
      inputEl.setSelectionRange(targetOffset, targetOffset);
      setTimeout(() => {
        if (inputEl) {
          inputEl.focus();
          inputEl.setSelectionRange(targetOffset, targetOffset);
          this.refreshActiveSegment(newRawValue, inputEl);
          this.cdr.markForCheck();
        }
      }, 0);
    }

    this.rows[rowIndex].isDirty = true;
    this.updateUnsavedCount();
    this.refreshActiveSegment(newRawValue, inputEl);
    this.cdr.markForCheck();
  }






  getActiveColumnIndex(): number {
    if (this.activeRowIndex === null || this.activeRowIndex < 0 || this.activeRowIndex >= this.rows.length) {
      return -1;
    }
    const row = this.rows[this.activeRowIndex];
    if (!row || !row.rawValue) {
      return this.activeSegment ?? 0;
    }

    const parts = row.rawValue.split('|');
    const segmentIdx = this.activeSegment ?? 0;

    // 1. Positional matching for mandatory first 4 fields (indices 0..3)
    if (segmentIdx >= 0 && segmentIdx <= 3) {
      return segmentIdx;
    }

    // Lock to header index captured on focus while actively typing to eliminate mid-character tab shifting
    if (this.isActivelyTyping) {
      return this.activeHeaderIndexOnFocus !== null ? this.activeHeaderIndexOnFocus : segmentIdx;
    }

    // 2. Extract text value at the active segment cursor index
    const segVal = (parts[segmentIdx] || '').trim();

    // Smart fallback to next logical unfilled header if segment is empty
    if (!segVal) {
      const parsed = this.parsePipeString(row.rawValue);
      // Fallback to first unfilled optional field in logical sequence
      if (!parsed.contactName) return 4;
      if (!parsed.phone) return 5;
      if (!parsed.email) return 6;
      if (!parsed.deliveryTime && !parsed.pickupTime) return 7;
      if (!parsed.delPU) return 8;
      if (!parsed.deliveryDate && !parsed.pickupDate) return 9;
      if (!parsed.driver_name) return 10;

      return segmentIdx;
    }

    // 3. Smart value evaluation for completed/non-empty segment values
    // Contact Email
    if (segVal.includes('@')) {
      return 6;
    }

    // Date (DD-MM-YYYY / YYYY-MM-DD)
    const dateRegex = /^\d{4}[-/]\d{2}[-/]\d{2}$|^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/;
    if (dateRegex.test(segVal)) {
      return 9;
    }

    // Committed Time
    const timeRegex = /^\d{1,2}:\d{2}(?:\s*[APap][Mm])?$|^\d{1,2}\s*[APap][Mm]$/;
    if (timeRegex.test(segVal)) {
      return 7;
    }

    // Del/PU
    const numVal = Number(segVal);
    if (!isNaN(numVal) && segVal !== '' && numVal >= 0 && numVal <= 200) {
      return 8;
    }

    // Contact Phone No
    const cleanDigits = segVal.replace(/\D/g, '');
    const isDigitsOnly = /^\+?[\d\s\-\(\)]+$/.test(segVal);
    if (isDigitsOnly && cleanDigits.length >= 3) {
      return 5;
    }

    // Match parsed row assignments for Contact Name vs Driver using sequential text order
    const partsForText = row.rawValue.split('|').map(s => s.trim());
    const matchedIdx = new Set<number>();

    for (let i = 4; i < partsForText.length; i++) {
      const val = partsForText[i];
      if (!val) continue;
      if (val.includes('@')) { matchedIdx.add(i); continue; }
      if (/^\d{4}[-/]\d{2}[-/]\d{2}$|^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(val)) { matchedIdx.add(i); continue; }
      if (/^\d{1,2}:\d{2}(?:\s*[APap][Mm])?$|^\d{1,2}\s*[APap][Mm]$/.test(val)) { matchedIdx.add(i); continue; }
      const numVal = Number(val);
      if (!isNaN(numVal) && numVal >= 0 && numVal <= 200) { matchedIdx.add(i); continue; }
      const cleanDigits = val.replace(/\D/g, '');
      if (/^\+?[\d\s\-\(\)]+$/.test(val) && cleanDigits.length >= 3) { matchedIdx.add(i); continue; }
    }

    const remainingTextIndices: number[] = [];
    for (let i = 4; i < partsForText.length; i++) {
      if (!matchedIdx.has(i) && partsForText[i].length > 0) {
        remainingTextIndices.push(i);
      }
    }

    const parsed = this.parsePipeString(row.rawValue);
    if (parsed.companyAddress && segVal === parsed.companyAddress) {
      return 3;
    }

    if (remainingTextIndices.length >= 2) {
      if (parsed.driver_name && segVal === parsed.driver_name) return 10;
      if (parsed.contactName && segVal === parsed.contactName) return 4;
      if (segmentIdx === remainingTextIndices[0]) return 4;
      if (segmentIdx === remainingTextIndices[1]) return 10;
    } else if (remainingTextIndices.length === 1) {
      if (parsed.driver_name && segVal === parsed.driver_name) return 10;
      if (parsed.contactName && segVal === parsed.contactName) return 4;
    }

    // Default positional fallback
    return segmentIdx;
  }

  onInputScroll(event: Event): void {
    // No-op, horizontal scroll is now handled on the parent cell-wrapper
  }

  refreshActiveSegment(rawValue: string, inputEl: HTMLInputElement): void {
    const selectionStart = inputEl.selectionStart;
    const textBeforeCursor = rawValue.substring(0, selectionStart || 0);
    this.activeSegment = (textBeforeCursor.match(/\|/g) || []).length;
    this.cdr.markForCheck();

    // Sync overlay scrollLeft and center cursor after DOM updates
    setTimeout(() => {
      const parent = inputEl.parentElement;
      if (parent) {
        const typedSpan = parent.querySelector('.overlay-typed') as HTMLElement;
        const overlayEl = parent.querySelector('.cell-overlay') as HTMLElement;

        if (typedSpan && overlayEl) {
          const originalText = typedSpan.innerText;
          const currentSelectionStart = inputEl.selectionStart || 0;
          typedSpan.innerText = rawValue.substring(0, currentSelectionStart);
          const cursorOffset = typedSpan.offsetWidth;
          typedSpan.innerText = originalText; // Restore original full text

          const containerWidth = parent.clientWidth;
          const currentScrollLeft = parent.scrollLeft;
          const totalTextWidth = typedSpan.offsetWidth;

          let targetScrollLeft = currentScrollLeft;

          // If the entire text fits within the container, do not scroll
          if (totalTextWidth < containerWidth) {
            targetScrollLeft = 0;
          } else {
            // Only scroll if the cursor goes outside the visible viewport boundary
            if (cursorOffset > currentScrollLeft + containerWidth - 120) {
              targetScrollLeft = cursorOffset - containerWidth + 120;
            } else if (cursorOffset < currentScrollLeft + 40) {
              targetScrollLeft = cursorOffset - 40;
            }
          }

          // Constrain targetScrollLeft between 0 and max scrollable width
          const maxScrollLeft = parent.scrollWidth - containerWidth;
          if (targetScrollLeft < 0) targetScrollLeft = 0;
          if (targetScrollLeft > maxScrollLeft) targetScrollLeft = maxScrollLeft;

          parent.scrollLeft = targetScrollLeft;
        } else {
          // Fallback if elements not found
          if (inputEl.selectionStart === (rawValue || '').length) {
            parent.scrollLeft = parent.scrollWidth;
          }
        }
      }
    }, 100);
  }



  getFilledStatusMap(rawValue: string): { [key: string]: boolean } {
    const status: { [key: string]: boolean } = {
      'Order No*': false,
      'Pickup/Delivery*': false,
      'Company Name*': false,
      'Company Address*': false,
      'Contact Name': false,
      'Contact Phone No': false,
      'Contact Email': false,
      'Committed Time': false,
      'Del/PU': false,
      'Date (DD-MM-YYYY)': false,
      'Driver': false
    };

    if (!rawValue) return status;
    const parsed = this.parsePipeString(rawValue);
    const parts = rawValue.split('|').map(s => s.trim()).filter(s => s.length > 0);

    status['Order No*'] = !!parsed.orderNo;
    status['Pickup/Delivery*'] = !!parsed.warehouse;
    status['Company Name*'] = !!parsed.companyName;
    status['Company Address*'] = !!parsed.companyAddress;
    status['Contact Name'] = !!parsed.contactName;
    status['Contact Phone No'] = !!parsed.phone;
    status['Contact Email'] = !!parsed.email;
    status['Committed Time'] = !!(parsed.deliveryTime || parsed.pickupTime);
    status['Date (DD-MM-YYYY)'] = !!(parsed.deliveryDate || parsed.pickupDate);
    status['Driver'] = !!parsed.driver_name;

    status['Del/PU'] = parts.some(p => {
      const num = Number(p);
      return !isNaN(num) && p !== '' && num >= 0 && num <= 200;
    });

    return status;
  }

  getValidationMap(rawValue: string): { [key: string]: 'valid' | 'invalid' | 'empty' } {
    const map: { [key: string]: 'valid' | 'invalid' | 'empty' } = {
      'Order No*': 'empty',
      'Pickup/Delivery*': 'empty',
      'Company Name*': 'empty',
      'Company Address*': 'empty',
      'Contact Name': 'empty',
      'Contact Phone No': 'empty',
      'Contact Email': 'empty',
      'Committed Time': 'empty',
      'Del/PU': 'empty',
      'Date (DD-MM-YYYY)': 'empty',
      'Driver': 'empty'
    };

    if (!rawValue) return map;
    const parsed = this.parsePipeString(rawValue);
    const parts = rawValue.split('|').map(s => s.trim()).filter(s => s.length > 0);

    if (parsed.orderNo) map['Order No*'] = 'valid';
    if (parsed.warehouse) map['Pickup/Delivery*'] = 'valid';
    if (parsed.companyName) map['Company Name*'] = 'valid';
    if (parsed.companyAddress) map['Company Address*'] = 'valid';
    if (parsed.contactName) map['Contact Name'] = 'valid';

    if (parsed.phone) {
      const cleanDigits = parsed.phone.replace(/\D/g, '');
      const isValid = cleanDigits.length === 10;
      map['Contact Phone No'] = isValid ? 'valid' : 'invalid';
    }

    if (parsed.email) {
      const isValid = parsed.email.includes('@') && parsed.email.includes('.');
      map['Contact Email'] = isValid ? 'valid' : 'invalid';
    }

    if (parsed.deliveryTime || parsed.pickupTime) map['Committed Time'] = 'valid';
    if (parsed.deliveryDate || parsed.pickupDate) map['Date (DD-MM-YYYY)'] = 'valid';
    if (parsed.driver_name) map['Driver'] = 'valid';

    const hasDelPU = parts.some(p => {
      const num = Number(p);
      return !isNaN(num) && p !== '' && num >= 0 && num <= 200;
    });
    if (hasDelPU) map['Del/PU'] = 'valid';

    return map;
  }

  getOverlayParts(rawValue: string): { text: string; isTransparent: boolean; isStar: boolean; isPipe: boolean; isActive: boolean }[] {
    const typed = rawValue || '';
    const parts: { text: string; isTransparent: boolean; isStar: boolean; isPipe: boolean; isActive: boolean }[] = [];

    parts.push({ text: typed, isTransparent: true, isStar: false, isPipe: false, isActive: false });

    // Determine the status of filled fields using smart type detection
    const statusMap = this.getFilledStatusMap(typed);

    // 2. Add placeholder parts
    const pipesCount = (typed.match(/\|/g) || []).length;

    let remainingLabels: string[] = [];

    if (pipesCount < 4) {
      remainingLabels = this.columnLabels.slice(pipesCount);
    } else {
      const optionalLabels = this.columnLabels.slice(4);
      remainingLabels = optionalLabels.filter(label => !statusMap[label]);
    }

    if (remainingLabels.length === 0) {
      return parts;
    }

    // Determine if we need to skip the active label to avoid overlap
    let startIndex = 0;
    let prependPipe = false;

    if (typed.endsWith('|')) {
      parts.push({ text: ' ', isTransparent: false, isStar: false, isPipe: false, isActive: false });
    } else if (!typed.endsWith(' ') && typed.length > 0) {
      startIndex = pipesCount < 4 ? 1 : 0;
      prependPipe = true;
    }

    const labelsToRender = remainingLabels.slice(startIndex);

    for (let idx = 0; idx < labelsToRender.length; idx++) {
      if (idx > 0 || prependPipe) {
        parts.push({ text: ' | ', isTransparent: false, isStar: false, isPipe: true, isActive: false });
        prependPipe = false;
      }

      const label = labelsToRender[idx];
      const isActive = (idx === 0);

      if (label.endsWith('*')) {
        const cleanName = label.slice(0, -1);
        parts.push({ text: cleanName, isTransparent: false, isStar: false, isPipe: false, isActive });
        parts.push({ text: '*', isTransparent: false, isStar: true, isPipe: false, isActive });
      } else {
        parts.push({ text: label, isTransparent: false, isStar: false, isPipe: false, isActive });
      }
    }

    // Add trailing pipe at the very end
    if (labelsToRender.length > 0) {
      parts.push({ text: ' |', isTransparent: false, isStar: false, isPipe: true, isActive: false });
    }

    return parts;
  }

  getExpectedLabelForSegment(textBeforeCursor: string): string {
    const pipesCount = (textBeforeCursor.match(/\|/g) || []).length;
    if (pipesCount < 4) {
      return this.columnLabels[pipesCount];
    }
    const statusMap = this.getFilledStatusMap(textBeforeCursor);
    const optionalLabels = this.columnLabels.slice(4);
    const remainingLabels = optionalLabels.filter(label => !statusMap[label]);
    return remainingLabels.length > 0 ? remainingLabels[0] : '';
  }

  evaluateShortcuts(textBeforeCursor: string): { replaced: boolean; newBefore: string } {
    const expectedLabel = this.getExpectedLabelForSegment(textBeforeCursor);
    const lastPipeIdx = textBeforeCursor.lastIndexOf('|');
    const textBeforeCurrentSegment = textBeforeCursor.substring(0, lastPipeIdx + 1);
    const typedInSegment = textBeforeCursor.substring(lastPipeIdx + 1).trim().toLowerCase();

    if (!typedInSegment) {
      return { replaced: false, newBefore: textBeforeCursor };
    }

    let textToInsert = '';
    let replaced = false;

    const today = new Date();
    const curMonth = String(today.getMonth() + 1).padStart(2, '0');
    const curYear = today.getFullYear();

    const pipesCount = (textBeforeCursor.match(/\|/g) || []).length;
    const dateRegexInText = /\b(?:\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4})\b/;
    const hasDateAlready = dateRegexInText.test(textBeforeCurrentSegment) || !!this.getFilledStatusMap(textBeforeCurrentSegment)['Date (DD-MM-YYYY)'];

    // 1. Date Shortcuts (td/td- for today, tm/tm- for tomorrow, DD-, DD-MM-, DD-MM) - only after mandatory fields (pipesCount >= 4) & if date not already present
    if (pipesCount >= 4 && !hasDateAlready) {
      if (typedInSegment === 'td' || typedInSegment === 'td-') {
        const curDay = String(today.getDate()).padStart(2, '0');
        textToInsert = `${curDay}-${curMonth}-${curYear}`;
        replaced = true;
      } else if (typedInSegment === 'tm' || typedInSegment === 'tm-') {
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        const tmDay = String(tomorrow.getDate()).padStart(2, '0');
        const tmMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
        textToInsert = `${tmDay}-${tmMonth}-${tomorrow.getFullYear()}`;
        replaced = true;
      } else {
        // DD-MM- or DD-MM format: e.g. "29-08-" or "29-08" -> "29-08-2026"
        const ddMmMatch = typedInSegment.match(/^([0-2]?[1-9]|10|20|30|31)-(0?[1-9]|1[0-2])-?$/);
        if (ddMmMatch) {
          const d = ddMmMatch[1].padStart(2, '0');
          const m = ddMmMatch[2].padStart(2, '0');
          textToInsert = `${d}-${m}-${curYear}`;
          replaced = true;
        } else {
          // DD- format: e.g. "29-" -> "29-07-2026"
          const ddMatch = typedInSegment.match(/^([0-2]?[1-9]|10|20|30|31)-$/);
          if (ddMatch) {
            const d = ddMatch[1].padStart(2, '0');
            textToInsert = `${d}-${curMonth}-${curYear}`;
            replaced = true;
          }
        }
      }
    }

    // 2. Time Shortcuts (if not matched as date) - requires explicit suffix (a, p, am, pm)
    const hasTimeAlready = !!(this.getFilledStatusMap(textBeforeCurrentSegment)['Committed Time']);
    if (!replaced && pipesCount >= 4 && !hasTimeAlready) {
      const timeMatch = typedInSegment.match(/^(\d{1,2})(a|p|am|pm)$/);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1], 10);
        const suffix = timeMatch[2];
        if (suffix && hour >= 1 && hour <= 12) {
          const ampm = suffix.startsWith('p') ? 'PM' : 'AM';
          textToInsert = `${hour.toString().padStart(2, '0')}:00 ${ampm}`;
          replaced = true;
        }
      }
    }

    if (replaced) {
      const beforeSegment = textBeforeCursor.substring(0, lastPipeIdx + 1);
      const newBefore = beforeSegment + (beforeSegment.endsWith(' ') ? '' : ' ') + textToInsert + ' | ';
      return { replaced: true, newBefore };
    }

    return { replaced: false, newBefore: textBeforeCursor };
  }

  onEnterKey(index: number, event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (this.driverDropdownVisible || this.companyDropdownVisible || this.addressDropdownVisible) {
      // Dropdown is open — Enter is handled by onKeyDown, do not navigate rows
      return;
    }

    const inputs = Array.from(document.querySelectorAll('.cell-input')) as HTMLInputElement[];
    const inputEl = inputs[index];
    if (!inputEl) return;

    const value = inputEl.value || '';
    const selectionStart = inputEl.selectionStart ?? value.length;
    const textBeforeCursor = value.substring(0, selectionStart);
    const after = value.substring(selectionStart);

    const pipesCount = (textBeforeCursor.match(/\|/g) || []).length;
    const nextPipeIndex = after.indexOf('|');

    // 1. Jump to next existing pipe segment if there is a pipe ahead in the row
    if (nextPipeIndex !== -1) {
      const newPos = selectionStart + nextPipeIndex + 1;
      const actualPos = (after.charAt(nextPipeIndex + 1) === ' ') ? newPos + 1 : newPos;
      inputEl.setSelectionRange(actualPos, actualPos);
      this.isActivelyTyping = false;
      this.refreshActiveSegment(value, inputEl);
      this.activeHeaderIndexOnFocus = this.getActiveColumnIndex();
      return;
    }

    // 2. Advance to next field in the same row if remaining fields exist
    if (pipesCount < 10) {
      const lastPipeInBefore = textBeforeCursor.lastIndexOf('|');
      const currentSegText = textBeforeCursor.substring(lastPipeInBefore + 1).trim();

      if (currentSegText) {
        let newBefore = textBeforeCursor;
        if (newBefore.endsWith('. ')) {
          newBefore = newBefore.slice(0, -2) + ' | ';
        } else if (newBefore.endsWith(' ')) {
          newBefore = newBefore.slice(0, -1) + ' | ';
        } else {
          newBefore = newBefore + ' | ';
        }

        const newValue = newBefore + after;
        this.rows[index].rawValue = newValue;
        inputEl.value = newValue;

        const newPos = newBefore.length;
        inputEl.setSelectionRange(newPos, newPos);
        this.rows[index].isDirty = true;
        this.updateUnsavedCount();
        this.isActivelyTyping = false;
        this.refreshActiveSegment(newValue, inputEl);
        this.activeHeaderIndexOnFocus = this.getActiveColumnIndex();
        return;
      }
    }

    // Row is complete — do nothing, user can Tab or click to move
  }

  moveToNextRow(index: number): void {
    if (index >= this.rows.length - 1) {
      this.addRow();
    }
    setTimeout(() => {
      const inputs = Array.from(document.querySelectorAll('.cell-input')) as HTMLInputElement[];
      if (inputs[index + 1]) {
        inputs[index + 1].focus();
        inputs[index + 1].setSelectionRange(0, 0);
      }
    }, 50);
  }

  onTabKey(event: Event, index: number): void {
    const inputEl = event.target as HTMLInputElement;
    const value = inputEl.value || '';
    const selectionStart = inputEl.selectionStart;

    if (selectionStart !== null) {
      let textBeforeCursor = value.substring(0, selectionStart);
      const after = value.substring(selectionStart);

      const pipesCount = (textBeforeCursor.match(/\|/g) || []).length;
      const nextPipeIndex = after.indexOf('|');

      // Check shortcuts first
      const shortcutResult = this.evaluateShortcuts(textBeforeCursor);

      if (shortcutResult.replaced) {
        event.preventDefault();
        const newValue = shortcutResult.newBefore + after;
        this.rows[index].rawValue = newValue;
        inputEl.value = newValue;

        const newPos = shortcutResult.newBefore.length;
        inputEl.setSelectionRange(newPos, newPos);
        this.rows[index].isDirty = true;
        this.updateUnsavedCount();
        this.isActivelyTyping = false;
        this.refreshActiveSegment(newValue, inputEl);
        this.activeHeaderIndexOnFocus = this.getActiveColumnIndex();
        return;
      }

      // If they are not in the last segment (index 10, Driver) or there are more pipes ahead
      if (pipesCount < 10 || nextPipeIndex !== -1) {
        event.preventDefault(); // Stop standard tab focus navigation

        if (nextPipeIndex !== -1) {
          const newPos = selectionStart + nextPipeIndex + 1;
          const actualPos = (after.charAt(nextPipeIndex + 1) === ' ') ? newPos + 1 : newPos;
          inputEl.setSelectionRange(actualPos, actualPos);
          this.isActivelyTyping = false;
          this.refreshActiveSegment(value, inputEl);
          this.activeHeaderIndexOnFocus = this.getActiveColumnIndex();
        } else if (pipesCount < 10) {
          const lastPipeInBefore = textBeforeCursor.lastIndexOf('|');
          const currentSegText = textBeforeCursor.substring(lastPipeInBefore + 1).trim();

          // Do NOT create empty pipes if current segment has no text
          if (!currentSegText) {
            if (pipesCount >= 4) {
              this.onEnterKey(index);
            }
            return;
          }

          // Create a new pipe separator when current segment contains text
          let newBefore = textBeforeCursor;
          if (newBefore.endsWith('. ')) {
            newBefore = newBefore.slice(0, -2) + ' | ';
          } else if (newBefore.endsWith(' ')) {
            newBefore = newBefore.slice(0, -1) + ' | ';
          } else {
            newBefore = newBefore + ' | ';
          }

          const newValue = newBefore + after;
          this.rows[index].rawValue = newValue;
          inputEl.value = newValue;

          const newPos = newBefore.length;
          inputEl.setSelectionRange(newPos, newPos);
          this.rows[index].isDirty = true;
          this.updateUnsavedCount();
          this.isActivelyTyping = false;
          this.refreshActiveSegment(newValue, inputEl);
          this.activeHeaderIndexOnFocus = this.getActiveColumnIndex();
        }
        return;
      }
    }

    // Default Tab behavior for the last segment (Driver): focus next row (or add one if last)
    event.preventDefault();

    if (index < this.rows.length - 1) {
      // Focus the start of the next row
      setTimeout(() => {
        const inputs = Array.from(document.querySelectorAll('.cell-input')) as HTMLInputElement[];
        if (inputs[index + 1]) {
          inputs[index + 1].focus();
          inputs[index + 1].setSelectionRange(0, 0);
        }
      }, 50);
    } else {
      // If it's the last row, add a new row first, then focus it
      this.addRow();
      setTimeout(() => {
        const inputs = Array.from(document.querySelectorAll('.cell-input')) as HTMLInputElement[];
        const nextInput = inputs[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.setSelectionRange(0, 0);
        }
      }, 100);
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    const text = event.clipboardData?.getData('text') || '';
    if (!text) return;

    const hasLineBreaks = text.includes('\n') || text.includes('\r');
    const hasTabs = text.includes('\t');

    // If single-line text without tabs, pass through to browser standard paste
    if (!hasLineBreaks && !hasTabs) {
      return;
    }

    event.preventDefault();
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) return;

    let targetIdx = index;
    let pastedCount = 0;

    for (const line of lines) {
      let formattedPipeStr = '';
      if (line.includes('\t')) {
        formattedPipeStr = line.split('\t').map(col => col.trim()).filter(c => c.length > 0).join(' | ');
      } else if (line.includes('|')) {
        formattedPipeStr = line;
      } else {
        formattedPipeStr = line;
      }

      if (!formattedPipeStr) continue;

      if (targetIdx >= this.rows.length) {
        this.rows.push({ rawValue: '', isDirty: true, original: null });
      }

      this.rows[targetIdx].rawValue = formattedPipeStr;
      this.rows[targetIdx].isDirty = true;
      pastedCount++;
      targetIdx++;
    }

    this.updateUnsavedCount();
    this.toast.show('success', `Pasted ${pastedCount} order row(s) into sheet`);
    this.cdr.markForCheck();
  }

  goBack(): void {
    this.router.navigate(['/admin/bulk-order']);
  }

  isGmailUser(): boolean {
    const email = this.authService.getOrgEmail || '';
    return email.toLowerCase().endsWith('@gmail.com');
  }

  accessSheet(): void {
    this.isAccessingSheet = true;
    this.cdr.markForCheck();
    this.orderService.getDispatchSheetUrl().subscribe({
      next: (response: any) => {
        this.isAccessingSheet = false;
        if (response?.result?.sheet_url) {
          window.open(response.result.sheet_url, '_blank');
        } else {
          this.toast.show('error', 'Sheet URL not found. Please try again.');
        }
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error fetching dispatch sheet URL:', error);
        this.isAccessingSheet = false;
        this.toast.show('error', 'Failed to fetch sheet URL.');
        this.cdr.markForCheck();
      }
    });
  }

  private updateUnsavedCount(): void {
    this.unsavedCount = this.rows.filter(r => r.isDirty).length + this.deletedRowUuids.length;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.unsavedCount > 0) {
      $event.returnValue = true;
    }
  }

  async canDeactivate(): Promise<boolean> {
    if (this.unsavedCount > 0) {
      const res = await this.dialogService.confirm(
        'Discard Changes?',
        'You have unsaved changes. If you leave, your changes will be lost. Do you want to discard them and leave?'
      );
      return !!res?.confirmed;
    }
    return true;
  }

  // ========== ARCHIVED SHEETS MODAL METHODS ==========
  openArchivedSheetsModal(): void {
    this.logDateFilter = new Date().toISOString().split('T')[0];
    this.showArchivedSheetsModal = true;
    this.cdr.markForCheck();
    this.fetchArchivedSheetsLogs();
  }

  closeArchivedSheetsModal(): void {
    this.showArchivedSheetsModal = false;
    this.logDateFilter = new Date().toISOString().split('T')[0];
    this.cdr.markForCheck();
  }

  fetchArchivedSheetsLogs(): void {
    this.isLoadingSheetsLogs = true;
    this.archivedSheetsLogs = [];
    let formattedDate: string | undefined = undefined;
    if (this.logDateFilter) {
      const parts = this.logDateFilter.split('-');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else {
        formattedDate = this.logDateFilter;
      }
    }
    this.orderService.getArchivedSheetsLogs(formattedDate).subscribe({
      next: (response: any) => {
        this.isLoadingSheetsLogs = false;
        if (response && response.result) {
          this.archivedSheetsLogs = response.result;
        } else {
          this.archivedSheetsLogs = [];
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isLoadingSheetsLogs = false;
        console.error('Error fetching archived sheets logs:', error);
        this.toast.show('error', 'Failed to load archived sheets logs');
        this.cdr.markForCheck();
      }
    });
  }

  exportArchivedSheet(log: any): void {
    if (!log?.archived_sheet_url) {
      this.toast.show('error', 'Sheet URL is not available');
      return;
    }
    log.isExporting = true;
    this.orderService.exportArchivedOrderSheet(log.archived_sheet_url).subscribe({
      next: (blob: Blob) => {
        log.isExporting = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const name = log.archived_sheet_name || 'archived-sheet';
        a.download = name.endsWith('.xlsx') || name.endsWith('.csv') || name.endsWith('.xls') ? name : `${name}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toast.show('success', 'Sheet downloaded successfully');
        this.cdr.markForCheck();
      },
      error: (error) => {
        log.isExporting = false;
        console.error('Error downloading archived sheet:', error);
        this.toast.show('error', 'Failed to download sheet');
        this.cdr.markForCheck();
      }
    });
  }

  // ========== SMART AUTO-MERGE & BACKGROUND POLLING ==========
  private pollLatestServerChanges(): void {
    this.orderService.getDispatchSheetRows().subscribe({
      next: (response: any) => {
        if (response?.resultCode === '1' && Array.isArray(response.result)) {
          this.mergeServerRows(response.result);
          this.lastSyncedAt = new Date();
          this.cdr.markForCheck();
        }
      },
      error: () => { }
    });
  }

  private mergeServerRows(serverItems: any[], resetDirtyState: boolean = true): void {
    const serverMap = new Map<string, any>();
    serverItems.forEach(item => {
      if (item.row_uuid) {
        serverMap.set(item.row_uuid, item);
      }
    });

    const updatedRows: SheetRow[] = [];
    const processedUuids = new Set<string>();

    const findUnmatchedServerItem = (localRawStr: string) => {
      const localOrderNo = (localRawStr.split('|')[0] || '').trim().toLowerCase();
      for (const item of serverItems) {
        if (item.row_uuid && !processedUuids.has(item.row_uuid)) {
          const sStr = item.order_string || '';
          const serverOrderNo = (sStr.split('|')[0] || '').trim().toLowerCase();
          if (serverOrderNo && localOrderNo && serverOrderNo === localOrderNo) {
            return item;
          }
          if (sStr.trim() === localRawStr.trim()) {
            return item;
          }
        }
      }
      return null;
    };

    // 1. Process existing local rows
    this.rows.forEach(localRow => {
      if (localRow.row_uuid && serverMap.has(localRow.row_uuid)) {
        const serverItem = serverMap.get(localRow.row_uuid)!;
        processedUuids.add(localRow.row_uuid);

        if (localRow.isDirty) {
          // Smart segment-level merge
          const origStr = localRow.original?.order_string || '';
          const mergedStr = this.mergePipeStrings(serverItem.order_string || '', localRow.rawValue || '', origStr);
          updatedRows.push({
            ...localRow,
            rawValue: mergedStr,
            isDirty: resetDirtyState ? false : true,
            original: serverItem
          });
        } else {
          // Not dirty — accept server string directly
          updatedRows.push({
            ...localRow,
            id: serverItem.order_id || serverItem.id || localRow.id || '',
            row_uuid: serverItem.row_uuid,
            rawValue: this.cleanOrderString(serverItem.order_string || ''),
            isDirty: false,
            original: serverItem
          });
        }
      } else if (!localRow.row_uuid && localRow.rawValue.trim() !== '') {
        // Check if server returned a newly created row matching this unsaved local row
        const matchedItem = findUnmatchedServerItem(localRow.rawValue);
        if (matchedItem) {
          processedUuids.add(matchedItem.row_uuid);
          updatedRows.push({
            ...localRow,
            id: matchedItem.order_id || matchedItem.id || '',
            row_uuid: matchedItem.row_uuid,
            rawValue: this.cleanOrderString(matchedItem.order_string || localRow.rawValue),
            isDirty: resetDirtyState ? false : true,
            original: matchedItem
          });
        } else {
          // Unsaved local row still pending save — preserve it
          updatedRows.push(localRow);
        }
      }
    });

    // 2. Append new server rows created by other admins or newly returned from API
    serverItems.forEach(serverItem => {
      if (serverItem.row_uuid && !processedUuids.has(serverItem.row_uuid)) {
        updatedRows.push({
          id: serverItem.order_id || serverItem.id || '',
          row_uuid: serverItem.row_uuid,
          rawValue: this.cleanOrderString(serverItem.order_string || ''),
          isDirty: false,
          original: serverItem
        });
      }
    });

    this.rows = updatedRows;
    this.updateUnsavedCount();
  }

  private mergePipeStrings(serverStr: string, localStr: string, originalStr: string): string {
    if (!localStr) return serverStr;
    if (!originalStr || serverStr === localStr) return localStr || serverStr;

    // Normalize all strings to align optional fields, preventing ghost data and false-positive edits
    serverStr = this.cleanOrderString(serverStr);
    localStr = this.cleanOrderString(localStr);
    originalStr = this.cleanOrderString(originalStr);

    const serverParts = serverStr.split('|');
    const localParts = localStr.split('|');
    const origParts = originalStr.split('|');

    const maxLen = Math.max(serverParts.length, localParts.length, origParts.length);
    const mergedParts: string[] = [];

    for (let i = 0; i < maxLen; i++) {
      const sSeg = (serverParts[i] ?? '').trim();
      const lSeg = (localParts[i] ?? '').trim();
      const oSeg = (origParts[i] ?? '').trim();

      if (lSeg !== oSeg) {
        // Local user modified this segment — keep local edit (Active Local Priority)
        mergedParts.push(lSeg);
      } else if (sSeg !== oSeg) {
        // Server / another admin updated this segment — accept server update
        mergedParts.push(sSeg);
      } else {
        // Untouched segment — default to server segment
        mergedParts.push(sSeg);
      }
    }

    return mergedParts.join(' | ');
  }
}
